/**
 * Trade setting templates.
 * Each template populates: savedAlgos, riskSize, maxRiskSize, contractLimit,
 * stackTrades, singleDirectionTrading, profitTarget, dailyLossLimit, dailyProfitLimit.
 *
 * savedAlgos format: ["AlgoName_TICKER_Timeframe", ...]
 * Use '' (empty string) for numeric fields you want left blank (e.g. profitTarget: '').
 *
 * To add a new template, just add another entry to the array below.
 *
 * Multi-group templates: set type: 'multi-group' and provide a `groups` array
 * instead of `settings`. Each group entry needs a `groupName` and the same
 * settings fields. Existing groups with the same name will be overwritten.
 *
 * Reference groups: use { groupName: 'Name', isReference: true, templateName: 'Poseidon Conservative $50k' }
 * to apply an existing single-group template's settings to that group name.
 */

export const TRADE_TEMPLATES = [
    {
        name: 'Prop Strategy A $50k',
        description: 'Poseidon All',
        type: 'multi-group',
        //Average days to pass = 10
        groups: [
            {
                groupName: 'Prop Strategy A $50k',
                isReference: true,
                templateName: 'Poseidon Aggressive $50k',
            }
        ]
    },
    {
        name: 'Prop Strategy A $50k Conservative',
        description: 'Poseidon Medium Conservative',
        type: 'multi-group',
        groups: [
            {
                groupName: 'Prop Strategy A $50k Conservative',
                isReference: true,
                templateName: 'Poseidon Conservative $50k',
            }
        ]
    },
    {
        name: 'Prop Strategy B $50k',
        description: 'Fortuna Conservative',
        type: 'multi-group',
        groups: [
            {
                groupName: 'Prop Strategy B $50k',
                isReference: true,
                templateName: 'Fortuna Conservative $50k',
            }
        ]
    },
    {
        name: 'Prop Strategy C $50k', //Average days = 15
        description: 'Poseidon + Fortuna',
        type: 'multi-group',
        groups: [
            {
                name: 'Prop Strategy C Group A',
                description: 'Poseidon Conservative',
                settings: {
                    riskSize: 400,
                    maxRiskSize: 1000,
                    profitTarget: '',
                    contractLimit: 100,
                    dailyLossLimit: 1500,
                    dailyProfitLimit: 3000,
                    stackTrades: true,
                    singleDirectionTrading: true,
                    savedAlgos: [
                        "Poseidon_MGC_5m-poseidon",
                        "Poseidon_MNQ_5m-poseidon"
                    ]
                },
            },
            {
                name: 'Prop Strategy C Group B',
                description: 'Fortuna Conservative',
                settings: {
                    riskSize: 100,
                    maxRiskSize: 500,
                    profitTarget: 2,
                    contractLimit: 100,
                    dailyLossLimit: 1500,
                    dailyProfitLimit: 3000,
                    stackTrades: true,
                    singleDirectionTrading: true,
                    maxTradesPerDay: 2,
                    savedAlgos: [
                        "Fortuna_MNQ_5m-fortuna",
                    ]
                },
            },
        ]
    },
    {
        name: 'Poseidon Conservative $50k',
        description: 'Poseidon balanced MES and MNQ',
        settings: {
            riskSize: 500,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Poseidon_MES_5m-poseidon",
                "Poseidon_MGC_5m-poseidon"
            ]
        },
    },
    {
        name: 'Poseidon Aggressive $50k',
        description: 'Higher risk Poseidon MES, MNQ, and MGC',
        settings: {
            riskSize: 500,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Poseidon_MES_5m-poseidon",
                "Poseidon_MGC_5m-poseidon",
                "Poseidon_MNQ_5m-poseidon"
            ]
        },
    },
    {
        name: 'Atlas Conservative $50k',
        description: 'Atlas Conservative MNQ',
        settings: {
            riskSize: 500,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 3000,
            dailyProfitLimit: 4000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Atlas_MNQ_10m-atlas",
            ]
        },
    },
    {
        name: 'Atlas Aggressive $50k',
        description: 'Atlas Aggressive MES and MNQ',
        settings: {
            riskSize: 750,
            maxRiskSize: 5000,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 3000,
            dailyProfitLimit: 4000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Atlas_MES_10m-atlas",
                "Atlas_MNQ_10m-atlas",
            ]
        },
    },
    {
        name: 'Ares Conservative $50k',
        description: 'Ares Conservative',
        settings: {
            riskSize: 250,
            maxRiskSize: 500,
            profitTarget: 2,
            contractLimit: 100,
            dailyLossLimit: 1000,
            dailyProfitLimit: 1000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Ares_MGC_1m-ares",
                "Ares_MGC_1m-ares-reversal",
            ]
        },
    },
    {
        name: 'Ares Aggressive $50k',
        description: 'Ares Aggressive',
        settings: {
            riskSize: 500,
            maxRiskSize: 750,
            profitTarget: 2,
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 2000,
            stackTrades: true,
            singleDirectionTrading: false,
            savedAlgos: [
                "Ares_MGC_1m-ares",
                "Ares_MGC_1m-ares-reversal",
            ]
        },
    },
    {
        name: 'Fortuna Conservative $50k',
        description: 'Fortuna Conservative',
        settings: {
            riskSize: 200,
            maxRiskSize: 500,
            profitTarget: 2,
            contractLimit: 100,
            dailyLossLimit: 440,
            dailyProfitLimit: 720,
            stackTrades: true,
            singleDirectionTrading: true,
            maxTradesPerDay: 2,
            savedAlgos: [
                "Fortuna_MNQ_5m-fortuna",
            ]
        },
    },
    {
        name: 'Fortuna Aggressive $50k',
        description: 'Fortuna Aggressive',
        settings: {
            riskSize: 400,
            maxRiskSize: 750,
            profitTarget: 2,
            contractLimit: 10,
            dailyLossLimit: 880,
            dailyProfitLimit: 1440,
            stackTrades: true,
            singleDirectionTrading: true,
            maxTradesPerDay: 2,
            savedAlgos: [
                "Fortuna_MNQ_5m-fortuna",
            ]
        },
    },
    {
        name: 'Kratos Conservative $50k',
        description: 'Kratos Conservative',
        settings: {
            riskSize: 500,
            maxRiskSize: 1500,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: false,
            singleDirectionTrading: true,
            savedAlgos: [
                "Kratos_MNQ_5m-kratos",
            ]
        },
    },
    {
        name: 'Kratos Aggressive $50k',
        description: 'Kratos Aggressive',
        settings: {
            riskSize: 750,
            maxRiskSize: 1500,
            profitTarget: '',
            contractLimit: 100,
            dailyLossLimit: 1500,
            dailyProfitLimit: 3000,
            stackTrades: false,
            singleDirectionTrading: true,
            savedAlgos: [
                "Kratos_MNQ_5m-kratos",
            ]
        },
    },

    // ---------------------------------------------------------------------
    // Smithfield account-tier templates (SPX/XSP 0DTE/1DTE options suite).
    // Per-algo contract counts follow ALM_Smithfield_Performance_4yr_v7_1.
    // Each tier is a multi-group template with one group per algo/position,
    // using riskMode: 'contracts' + numContracts to trade a fixed contract
    // count (Schwab connections only).
    //
    // SPX/XSP fallback: when a group lists both the SPX and XSP variant of an
    // algo, an SPX trade that collides with an existing position is redirected
    // to XSP and the contract count scales x10 automatically (XSP = 1/10 SPX).
    // We keep the single SPX numContracts here; the engine derives the XSP count.
    //
    // Good Morning and Phoenix support XSP. Double Dip, Market Power and Last
    // Call are SPX-only, so their groups list just the SPX algo and a colliding
    // trade is skipped rather than redirected. XSP-only positions list just the
    // XSP algo.
    //
    // These only appear for users whose algo access covers every algo listed
    // (checkAlgosAvailable gating in UserControls), so they are effectively
    // Smithfield-only.
    //
    // tiers: options templates are shown only to users on one of the listed
    // options tiers (Unlimited sees all). A template must fit the per-account caps
    // of every tier it lists (utils/optionsLimits.js). Income targets and minimum
    // account sizes in the descriptions mirror INCOME_TIERS in utils/constants.js.
    // ---------------------------------------------------------------------
    {
        name: 'Smithfield $10k',
        description: 'Double Dip x3, Market Power x1, Good Morning x1 · $500/mo target · min $14,000 account',
        type: 'multi-group',
        tiers: ['legacy', 'basic', 'premium'],
        groups: [
            {
                name: 'Smithfield $10k - Double Dip',
                description: 'Double Dip SPX x3',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 3,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["DoubleDip_SPX_0DTE"],
                },
            },
            {
                name: 'Smithfield $10k - Market Power',
                description: 'Market Power SPX x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["MarketPower_SPX_1DTE"],
                },
            },
            {
                name: 'Smithfield $10k - Good Morning',
                description: 'Good Morning XSP x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["GoodMorning_XSP_0DTE"],
                },
            },
        ],
    },
    {
        name: 'Smithfield $25k',
        description: 'Double Dip x3, Market Power x1, Good Morning x2 · $1,000/mo target · min $23,000 account',
        type: 'multi-group',
        tiers: ['legacy', 'basic', 'premium'],
        groups: [
            {
                name: 'Smithfield $25k - Double Dip',
                description: 'Double Dip SPX x3',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 3,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["DoubleDip_SPX_0DTE"],
                },
            },
            {
                name: 'Smithfield $25k - Market Power',
                description: 'Market Power SPX x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["MarketPower_SPX_1DTE"],
                },
            },
            {
                name: 'Smithfield $25k - Good Morning',
                description: 'Good Morning XSP x2',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 2,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["GoodMorning_XSP_0DTE"],
                },
            },
        ],
    },
    {
        name: 'Smithfield $50k',
        description: 'Double Dip x3, Market Power x1, Good Morning x2, Last Call x1 · $3,000/mo target · min $45,500 account',
        type: 'multi-group',
        tiers: ['legacy', 'basic', 'premium'],
        groups: [
            {
                name: 'Smithfield $50k - Double Dip',
                description: 'Double Dip SPX x3',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 3,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["DoubleDip_SPX_0DTE"],
                },
            },
            {
                name: 'Smithfield $50k - Market Power',
                description: 'Market Power SPX x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["MarketPower_SPX_1DTE"],
                },
            },
            {
                name: 'Smithfield $50k - Good Morning',
                description: 'Good Morning XSP x2',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 2,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["GoodMorning_XSP_0DTE"],
                },
            },
            {
                name: 'Smithfield $50k - Last Call',
                description: 'Last Call SPX x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["LastCall_SPX_0DTE"],
                },
            },
        ],
    },
    {
        name: 'Smithfield $100k',
        description: 'Double Dip x3, Market Power x1, Good Morning x1, Last Call x2 · $6,000/mo target · min $101,000 account',
        type: 'multi-group',
        tiers: ['legacy', 'basic', 'premium'],
        groups: [
            {
                name: 'Smithfield $100k - Double Dip',
                description: 'Double Dip SPX x3',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 3,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["DoubleDip_SPX_0DTE"],
                },
            },
            {
                name: 'Smithfield $100k - Market Power',
                description: 'Market Power SPX x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["MarketPower_SPX_1DTE"],
                },
            },
            {
                name: 'Smithfield $100k - Good Morning',
                description: 'Good Morning SPX x1 (XSP fallback)',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["GoodMorning_SPX_0DTE", "GoodMorning_XSP_0DTE"],
                },
            },
            {
                name: 'Smithfield $100k - Last Call',
                description: 'Last Call SPX x2',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 2,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["LastCall_SPX_0DTE"],
                },
            },
        ],
    },
    {
        name: 'Smithfield $100k Conservative',
        description: 'Double Dip x3, Market Power x1, Phoenix x1, Good Morning x1 · $6,000/mo target · min $101,000 account',
        type: 'multi-group',
        tiers: ['legacy', 'basic', 'premium'],
        groups: [
            {
                name: 'Smithfield $100k Conservative - Double Dip',
                description: 'Double Dip SPX x3',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 3,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["DoubleDip_SPX_0DTE"],
                },
            },
            {
                name: 'Smithfield $100k Conservative - Market Power',
                description: 'Market Power SPX x1',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["MarketPower_SPX_1DTE"],
                },
            },
            {
                name: 'Smithfield $100k Conservative - Phoenix',
                description: 'Phoenix SPX x1 (XSP fallback)',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["Phoenix_SPX_1DTE", "Phoenix_XSP_1DTE"],
                },
            },
            {
                name: 'Smithfield $100k Conservative - Good Morning',
                description: 'Good Morning SPX x1 (XSP fallback)',
                settings: {
                    riskMode: 'contracts',
                    numContracts: 1,
                    riskSize: '',
                    maxRiskSize: '',
                    profitTarget: '',
                    contractLimit: '',
                    dailyLossLimit: '',
                    dailyProfitLimit: '',
                    stackTrades: true,
                    singleDirectionTrading: false,
                    savedAlgos: ["GoodMorning_SPX_0DTE", "GoodMorning_XSP_0DTE"],
                },
            },
        ],
    },
];
